"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  AppDialog,
  type AppDialogVariant,
} from "@/components/ui/app-dialog";

export interface AppDialogOptions {
  title?: string;
  variant?: AppDialogVariant;
  confirmText?: string;
  cancelText?: string;
}

interface DialogState {
  isOpen: boolean;
  type: "alert" | "confirm";
  title: string;
  message: string;
  variant: AppDialogVariant;
  confirmText?: string;
  cancelText: string;
}

interface AppDialogContextValue {
  alert: (message: string, options?: AppDialogOptions) => Promise<void>;
  confirm: (message: string, options?: AppDialogOptions) => Promise<boolean>;
}

const defaultTitles: Record<AppDialogVariant, string> = {
  info: "Notice",
  success: "Success",
  warning: "Warning",
  danger: "Are you sure?",
};

const AppDialogContext = createContext<AppDialogContextValue | null>(null);

export function AppDialogProvider({ children }: { children: ReactNode }) {
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const closeDialog = useCallback((result: boolean) => {
    setDialog(null);
    resolveRef.current?.(result);
    resolveRef.current = null;
  }, []);

  const openDialog = useCallback(
    (
      type: "alert" | "confirm",
      message: string,
      options?: AppDialogOptions
    ): Promise<boolean> => {
      return new Promise((resolve) => {
        if (resolveRef.current) {
          resolveRef.current(false);
        }

        resolveRef.current = resolve;

        const variant =
          options?.variant ?? (type === "confirm" ? "danger" : "info");

        setDialog({
          isOpen: true,
          type,
          title: options?.title ?? defaultTitles[variant],
          message,
          variant,
          confirmText: options?.confirmText,
          cancelText: options?.cancelText ?? "Cancel",
        });
      });
    },
    []
  );

  const alert = useCallback(
    async (message: string, options?: AppDialogOptions) => {
      await openDialog("alert", message, options);
    },
    [openDialog]
  );

  const confirm = useCallback(
    (message: string, options?: AppDialogOptions) =>
      openDialog("confirm", message, options),
    [openDialog]
  );

  useEffect(() => {
    if (!dialog?.isOpen) return;

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeDialog(false);
      }
    };

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [dialog?.isOpen, closeDialog]);

  useEffect(() => {
    if (dialog?.isOpen) {
      document.body.style.overflow = "hidden";
      return;
    }

    document.body.style.overflow = "";
  }, [dialog?.isOpen]);

  return (
    <AppDialogContext.Provider value={{ alert, confirm }}>
      {children}
      {dialog && (
        <AppDialog
          isOpen={dialog.isOpen}
          type={dialog.type}
          title={dialog.title}
          message={dialog.message}
          variant={dialog.variant}
          confirmText={dialog.confirmText}
          cancelText={dialog.cancelText}
          onClose={() => closeDialog(false)}
          onConfirm={() => closeDialog(true)}
        />
      )}
    </AppDialogContext.Provider>
  );
}

export function useAppDialog() {
  const context = useContext(AppDialogContext);
  if (!context) {
    throw new Error("useAppDialog must be used within AppDialogProvider");
  }
  return context;
}